/**
 * Chat Interface Component - ממשק צ'אט
 * Main chat interface with message history, session sidebar, and real-time updates
 */

import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { RefreshCw, AlertCircle, ChevronsRight, ChevronsLeft, Lock, Brain } from 'lucide-react'
import MessageBubble from './MessageBubble'
import TypingIndicator from './TypingIndicator'
import ChatInput from './ChatInput'
import ChatHistory from './ChatHistory'
import MemoryPanel from './MemoryPanel'
import api, { handleApiError } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { ChatMessage, SendMessageResponse } from '../../types'

export default function ChatInterface() {
  const { user } = useAuthStore()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showUpgradeCta, setShowUpgradeCta] = useState(false)
  const [remainingMessages, setRemainingMessages] = useState(50)
  const [showHistory, setShowHistory] = useState(true)
  const [showMemory, setShowMemory] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // Lock body scroll. Also flag the chat as active so the floating
  // accessibility button can lift above the input bar on mobile
  // (otherwise the FAB and the RTL send button share the bottom-left corner).
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.body.classList.add('chat-active')
    return () => {
      document.body.style.overflow = prev
      document.body.classList.remove('chat-active')
    }
  }, [])

  // Auto-scroll the transcript to the bottom.
  // Scroll the messages container itself (via scrollTop) rather than calling
  // scrollIntoView on an anchor: scrollIntoView walks up to the nearest
  // scrollable ancestor, which on long conversations dragged the whole fixed
  // chat panel out of view and left the transcript looking blank while the
  // assistant was "thinking". Keeping the scroll contained fixes that.
  const scrollToBottom = () => {
    const el = messagesContainerRef.current
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Load most recent conversation on mount
  useEffect(() => {
    loadMostRecentConversation()
  }, [])

  const loadMostRecentConversation = async () => {
    try {
      const listRes = await api.get('/chat/conversations', {
        params: { status: 'active', limit: 1 }
      })
      if (listRes.data?.conversations && listRes.data.conversations.length > 0) {
        const id = listRes.data.conversations[0]._id
        await loadConversation(id)
      }
    } catch {
      // No existing conversation
    }
  }

  const loadConversation = async (id: string) => {
    try {
      const response = await api.get(`/chat/conversations/${id}`)
      if (response.data?.conversation) {
        const conv = response.data.conversation
        setConversationId(conv._id)
        setMessages(conv.messages || [])
        setError(null)
      }
    } catch (err) {
      setError(handleApiError(err))
    }
  }

  const handleSendMessage = async (messageText: string) => {
    setIsLoading(true)
    setError(null)

    // Add user message to UI immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: messageText,
      contentHash: '',
      timestamp: new Date().toISOString(),
      sanitized: false,
      flaggedForReview: false,
    }

    setMessages((prev) => [...prev, userMessage])

    try {
      const requestBody: { message: string; conversationId?: string } = {
        message: messageText,
      }

      // Only include conversationId if it exists
      if (conversationId) {
        requestBody.conversationId = conversationId
      }

      // The LLM can take well over the default 30s axios timeout to answer.
      // Give this request a generous timeout so a slow "thinking" turn isn't
      // aborted (which would drop the user's question and show an error).
      const response = await api.post<SendMessageResponse>('/chat/message', requestBody, {
        timeout: 120000,
      })

      const { conversation, response: assistantResponse, metadata } = response.data

      // Update conversation ID
      if (conversation.id && !conversationId) {
        setConversationId(conversation.id)
      }

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: assistantResponse,
        contentHash: '',
        timestamp: new Date().toISOString(),
        tokenCount: metadata.tokensUsed,
        sanitized: true,
        flaggedForReview: metadata.flagged,
        recommendationsReturned:
          metadata.recommendationsReturned > 0
            ? [`${metadata.recommendationsReturned} recommendations`]
            : undefined,
      }

      setMessages((prev) => [...prev, assistantMessage])

      // Update remaining messages
      setRemainingMessages((prev) => Math.max(0, prev - 1))
    } catch (err) {
      // Paywall: unpaid users may still ask, but instead of an advisory answer
      // they get a notice to arrange payment (no LLM call — backend returns 402).
      const status = (err as any)?.response?.status
      const code = (err as any)?.response?.data?.code
      if (status === 402 || code === 'SUBSCRIPTION_REQUIRED') {
        const upgradeMessage: ChatMessage = {
          role: 'assistant',
          content: 'להמשך שימוש ביועץ השקעות דיגיטלי, אנא הסדר תשלום.',
          contentHash: '',
          timestamp: new Date().toISOString(),
          sanitized: true,
          flaggedForReview: false,
        }
        setMessages((prev) => [...prev, upgradeMessage])
        setShowUpgradeCta(true)
        return
      }

      // Remove the optimistically added user message on other errors
      setMessages((prev) => prev.filter((m) => m !== userMessage))
      setError(handleApiError(err))

      // Add error message
      const errorMessage: ChatMessage = {
        role: 'system',
        content: `שגיאה: ${handleApiError(err)}`,
        contentHash: '',
        timestamp: new Date().toISOString(),
        sanitized: true,
        flaggedForReview: false,
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleNewConversation = () => {
    setConversationId(null)
    setMessages([])
    setError(null)
    setRemainingMessages(50)
  }

  const handleSelectConversation = (id: string) => {
    if (id === conversationId) return
    loadConversation(id)
  }

  return (
    <div className="fixed inset-0 lg:right-64 flex overflow-hidden" dir="rtl">
      {/* History sidebar — collapsible */}
      {showHistory && (
        <div className="w-72 h-full border-l border-gray-200 flex-shrink-0 relative z-10">
          <ChatHistory
            currentConversationId={conversationId}
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onClose={() => setShowHistory(false)}
          />
        </div>
      )}

      {/* Main chat area */}
      <div className="flex-1 bg-white flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-l from-primary-50 to-white flex-shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* History toggle */}
            <button
              onClick={() => setShowHistory((prev) => !prev)}
              className={`text-gray-500 hover:text-gray-700 transition-colors ${showHistory ? 'text-primary-600' : ''}`}
              title={showHistory ? 'הסתר היסטוריה' : 'הצג היסטוריה'}
              aria-label={showHistory ? 'הסתר היסטוריה' : 'הצג היסטוריה'}
            >
              {showHistory ? <ChevronsRight size={20} aria-hidden="true" /> : <ChevronsLeft size={20} aria-hidden="true" />}
            </button>
            <div className="min-w-0">
              <h2 className="text-base font-heading font-bold text-gray-900 truncate">צ'אט ייעוץ השקעות</h2>
              <p className="text-xs text-gray-500 truncate">יפתח ונגר - היועץ הדיגיטלי</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 mr-2">
            <button
              onClick={() => setShowMemory(true)}
              className="text-gray-500 hover:text-primary-600 transition-colors"
              title="מה יועצי זוכר עליי"
              aria-label="מה יועצי זוכר עליי"
            >
              <Brain size={18} aria-hidden="true" />
            </button>
            <button
              onClick={handleNewConversation}
              className="btn btn-outline text-sm"
              title="התחל שיחה חדשה"
            >
              <RefreshCw size={15} aria-hidden="true" />
              <span className="text-xs">שיחה חדשה</span>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-3"
          role="log"
          aria-live="polite"
          aria-relevant="additions"
          aria-label="הודעות הצ'אט"
        >
          {/* Welcome message */}
          {messages.length === 0 && (
            <div className="text-center py-8 px-4">
              <img
                src="/iftach-avatar.jpg"
                alt="יפתח ונגר"
                className="w-16 h-16 rounded-full object-cover border-2 border-primary-200 mx-auto mb-3"
              />
              <h3 className="text-lg font-heading font-bold text-gray-900 mb-1">
                שלום {user?.firstName}!
              </h3>
              <p className="text-base text-gray-600">
                יפתח ונגר יעוץ השקעות ופנסיוני, שלח הודעה ואני כאן לעזור.
              </p>
            </div>
          )}

          {/* Messages */}
          {messages.map((message, index) => (
            <MessageBubble
              key={`${message.timestamp}-${index}`}
              message={message}
              userName={`${user?.firstName} ${user?.lastName}`}
            />
          ))}

          {/* Typing indicator */}
          {isLoading && <TypingIndicator />}

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700" role="alert">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p>{error}</p>
            </div>
          )}

          {/* Upgrade CTA — shown after an unpaid user gets the payment notice */}
          {showUpgradeCta && (
            <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 text-center">
              <Lock size={20} className="mx-auto text-primary-600 mb-2" aria-hidden="true" />
              <Link to="/subscription" className="btn btn-primary inline-block">
                להסדרת התשלום
              </Link>
            </div>
          )}
        </div>

        {/* Input Area */}
        <ChatInput
          onSend={handleSendMessage}
          disabled={isLoading}
          remainingMessages={remainingMessages}
          maxMessages={50}
        />
      </div>

      {/* Memory panel — view / clear what יועצי remembers */}
      <MemoryPanel isOpen={showMemory} onClose={() => setShowMemory(false)} />
    </div>
  )
}
